// 📄 pages/Proyectos/CrearProyecto.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, FolderPlus, ArrowLeft, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useUserProjects } from '../../context/ProjectsContext.tsx';
import { createProject, ProjectRole } from '../../services/projectService.ts';
import { resolveOrgId } from '../../services/organizationService.ts';
import ProyectoForm, { ProyectoFormValues } from './components/ProyectoForm.tsx';
import { modulosDe, PLANTILLAS } from './modulosConfig.ts';
import { crearHitosDePlantilla } from '../../services/hitoService.ts';
import LimiteUsoAlerta from '../../components/ui/LimiteUsoAlerta.tsx';

const ROLE_OPTIONS: { value: ProjectRole; label: string }[] = [
  { value: 'PM', label: 'Project Manager' },
  { value: 'tecnico', label: 'Consultor Técnico' },
  { value: 'funcional', label: 'Consultor Funcional' }
];

export default function CrearProyecto() {
  const { user, profile, loading, orgActual } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rol, setRol] = useState<ProjectRole>('PM');

  // "Plantillas de proyecto" (2026-08-20, Fase 2) — a pedido del usuario:
  // no una entidad nueva a mantener, sino elegir CUALQUIER proyecto propio
  // existente como base y clonar su configuración básica (soluciones
  // activas + tipo/versión de ERP) — nunca cliente/nombre, eso es siempre
  // del proyecto NUEVO. `useUserProjects()` ya trae los proyectos del
  // usuario completos (mismo hook que Mis Proyectos), sin fetch aparte.
  const { proyectos: misProyectos } = useUserProjects();
  const [proyectoBaseId, setProyectoBaseId] = useState('');
  const proyectosBase = misProyectos.filter((p) => !p.archivado).sort((a, b) => a.nombre.localeCompare(b.nombre));
  const proyectoBase = proyectosBase.find((p) => p.id === proyectoBaseId);

  const handleSubmit = async (values: ProyectoFormValues) => {
    if (!user || !profile) throw new Error('Sin sesión activa.');
    const projectId = await createProject({
      clienteId: values.clienteId,
      clienteNombre: values.clienteNombre,
      nombre: values.nombre,
      solucionesActivas: values.solucionesActivas,
      modulos: values.modulos,
      plantilla: values.plantilla,
      areas: values.areas,
      erpTipo: values.erpTipo,
      erpVersion: values.erpVersion,
      creador: { userId: user.uid, rol, email: profile.email },
      orgId: resolveOrgId(orgActual, user.uid)
    });
    // Hitos sugeridos por la plantilla (G4). Si falla, el proyecto ya existe: se avisa en consola.
    const hitos = PLANTILLAS.find((p) => p.id === values.plantilla)?.hitos || [];
    await crearHitosDePlantilla(projectId, hitos, user.uid).catch((err) => console.error('Error creando los hitos de la plantilla:', err));
    navigate(`/proyecto/${projectId}`);
  };

  return (
    <div className="p-6 max-w-lg mx-auto text-white">
      <Link
        to="/proyectos"
        className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Mis Proyectos
      </Link>

      <h1 className="text-2xl font-light mb-6 flex items-center gap-2">
        <FolderPlus className="w-6 h-6 text-blue-400" /> Nuevo Proyecto
      </h1>

      <div className="mb-4">
        <LimiteUsoAlerta
          cantidad={profile?.cantidadProyectosCreados ?? 0}
          limite={profile?.limiteProyectos}
          etiqueta="proyectos"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <>
          {proyectosBase.length > 0 && (
            <div className="mb-4 bg-white/5 border border-white/10 rounded-2xl p-4">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                <Copy className="w-3.5 h-3.5" /> Basar en un proyecto existente (opcional)
              </label>
              <select
                value={proyectoBaseId}
                onChange={(e) => setProyectoBaseId(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Empezar en blanco</option>
                {proyectosBase.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.clienteNombre ? ` (${p.clienteNombre})` : ''}</option>)}
              </select>
              <p className="text-[11px] text-gray-500 mt-1.5">
                Copia los módulos, las soluciones activas y el tipo/versión de ERP — nunca el cliente, nombre, ni datos técnicos del proyecto elegido.
              </p>
            </div>
          )}

          <ProyectoForm
            key={proyectoBaseId || 'blank'}
            orgId={resolveOrgId(orgActual, user?.uid || '')}
            initialClienteId={searchParams.get('clienteId') || undefined}
            initialSolucionesActivas={proyectoBase?.solucionesActivas}
            initialModulos={proyectoBase ? modulosDe(proyectoBase) : undefined}
            initialAreas={proyectoBase?.areas}
            mostrarPlantillas
            initialErpTipo={proyectoBase?.erpTipo}
            initialErpVersion={proyectoBase?.erpVersion}
            submitLabel="Crear Proyecto"
            savingLabel="Creando..."
            onSubmit={handleSubmit}
            onCancel={() => navigate('/proyectos')}
          >
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              Tu rol en este proyecto
            </label>
            <select
              value={rol}
              onChange={(e) => setRol(e.target.value as ProjectRole)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          </ProyectoForm>
        </>
      )}
    </div>
  );
}
